import { useMemo, useCallback } from 'react'

import { Icon } from '@circlefin/components'
import { createColumnHelper } from '@circlefin/components/lib/AdvancedTable'
import { useDate, useMoney } from '@circlefin/hooks'
import { FeeActivity } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import type { InvoiceItem } from '@shared/graphql'

const columnHelper = createColumnHelper<InvoiceItem>()
const textClassNames = 'text-neutral type-body-base'

const dateTitleMap: Record<FeeActivity, string> = {
  [FeeActivity.cpnPayment]: 'billing.feeDetails.table.mintRowTitle',
  [FeeActivity.pushPayment]: 'billing.feeDetails.table.mintRowTitle',
  [FeeActivity.payout]: 'billing.feeDetails.table.withdrawalRowTitle',
  [FeeActivity.returnedPayout]: 'billing.feeDetails.table.mintRowTitle',
}

export const useRelatedTransactionsColumns = () => {
  const { t } = useTranslation('settings')
  const { date: formatDate } = useDate()
  const { money } = useMoney()

  const addWithdrawalSign = useCallback(({ amount, activity }: InvoiceItem) => {
    switch (activity) {
      case FeeActivity.payout:
        return `-${amount.amount}`
      case FeeActivity.cpnPayment:
      case FeeActivity.pushPayment:
      case FeeActivity.returnedPayout:
        return amount.amount
    }
  }, [])

  return useMemo(() => {
    /**
     * Date Column.
     */
    const date = columnHelper.accessor('createDate', {
      size: 120,
      cell: ({ getValue, row }) => {
        return (
          <div className="flex items-center gap-4">
            <Icon
              className="shrink-0"
              name="SwitchHorizontalOutline"
              size={20}
            />
            <div className="flex flex-col">
              <span className="text-neutral type-body-base-bold">
                {t(dateTitleMap[row.original.activity])}
              </span>
              <span className={textClassNames}>
                {formatDate(getValue(), 'monthDayYear')}
              </span>
            </div>
          </div>
        )
      },
    })

    /**
     * Amount Column.
     */
    const amount = columnHelper.accessor('amount', {
      size: 120,
      cell: ({ getValue, row }) => {
        const amount = addWithdrawalSign(row.original)

        return (
          <div className="flex items-center justify-end gap-4">
            <span className={textClassNames}>
              {money({
                number: amount,
                variant: getValue().currency,
                options: {
                  symbol: false,
                  signDisplay: true,
                },
              })}
            </span>
            <Icon name="ChevronRightOutline" size={24} />
          </div>
        )
      },
    })

    return [date, amount]
  }, [formatDate, money, t, addWithdrawalSign])
}
